import { type Breakpoint } from '@mui/material/styles';

import { type AtLeastOneProperty } from '@craft/lib';

import { ThemeBreakpoints } from './breakpoints';

export type FlatMediaQueryRange<L, T = L> = [L, null] | [L, T] | [null, T];

/**
 * Defines the manner in which a media query can be targeted in either the horizontal or vertical
 * direction.
 */
export type MediaQueryRange<L, T = L> =
  | { max: T; min: L }
  | { max: T }
  | { min: L }
  | FlatMediaQueryRange<L, T>;

export type RawMediaQueryObj = {
  height: MediaQueryRange<number>;
  width: MediaQueryRange<Exclude<Viewport, Viewport.Mobile> | number> | Viewport;
};

export type MediaQueryDimension = keyof RawMediaQueryObj;

/**
 * Represents the structured manner in which a media query can be defined in the application.
 *
 * @example
 * // Will result in a CSS media query representing heights between 40px and 409.95px, and widths
 * // between 0px and the width at which the viewport becomes Viewport.Medium (non exclusive).
 * const query: MediaQueryObj = { height: [40, 500], width: { max: Viewport.Medium } }
 */
export type MediaQueryObj = AtLeastOneProperty<RawMediaQueryObj>;

/**
 * Defines a discrete, mutually-exclusive window size in the application.
 *
 * A {@link Viewport} is defined by horizontal breakpoint boundaries that, when matched against the
 * current window size, indicate that the relevant viewport, {@link Viewport}, is active (or
 * represented by the current window size).
 *
 * The boundaries, {@link ViewportBreakpointBoundaries}, must cause the {@link Viewport}(s) to be
 * mutually exclusive.  In other words, there should never be a window size that will match more
 * than one viewport, {@link Viewport}, and there should never be a window size that will not match
 * any viewport, {@link Viewport}.
 *
 * If this condition is not met, based on the definitions of the {@link Viewports} (below), an
 * error will be thrown (and tests will fail).
 *
 * Note:
 * ----
 * This mutually exclusive treatment differs from that of a {@link View}.  With a {@link View}, the
 * active view does not need to be mutually exclusive for a given window size, and a given window
 * size can result in multiple {@link View}(s) being active.
 */
export enum Viewport {
  Large = 'Large',
  Medium = 'Medium',
  Mobile = 'Mobile',
  Small = 'Small',
  XLarge = 'XLarge',
}

type ViewportBreakpointBoundaries =
  | ['xl', 'infinity']
  | ['zero', 'sm']
  | [Exclude<Breakpoint, 'xl'>, Exclude<Breakpoint, 'sm'>];

export const Viewports = [
  {
    boundaries: ['lg', 'xl'],
    id: Viewport.Large,
  },
  {
    boundaries: ['md', 'lg'],
    id: Viewport.Medium,
  },
  {
    boundaries: ['zero', 'sm'],
    id: Viewport.Mobile,
  },
  {
    boundaries: ['sm', 'md'],
    id: Viewport.Small,
  },
  {
    boundaries: ['xl', 'infinity'],
    id: Viewport.XLarge,
  },
] as const satisfies {
  readonly boundaries: ViewportBreakpointBoundaries;
  readonly id: Viewport;
}[];

export type ViewportModel<S extends Viewport = Viewport> = Extract<
  (typeof Viewports)[number],
  { id: S }
>;

export const getViewportModel = <S extends Viewport>(viewport: S): ViewportModel<S> => {
  const model = Viewports.find(v => v.id === viewport);
  if (!model) {
    throw new Error(`No model found for viewport ${viewport}`);
  }
  return model as ViewportModel<S>;
};

export const getViewportLowerBoundary = (viewport: Viewport): number => {
  const model = getViewportModel(viewport);
  const lowerBoundary = model.boundaries[0];
  if (lowerBoundary === 'zero') {
    return 0;
  }
  return ThemeBreakpoints[lowerBoundary];
};

export const getViewportUpperBoundary = (viewport: Viewport): number => {
  const model = getViewportModel(viewport);
  const upperBoundary = model.boundaries[1];
  if (upperBoundary === 'infinity') {
    return Infinity;
  }
  return ThemeBreakpoints[upperBoundary];
};

export const isSizeWithinViewport = (size: number, viewport: Viewport): boolean =>
  size >= getViewportLowerBoundary(viewport) && size < getViewportUpperBoundary(viewport);
